import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarClock, Info, Lock, Save, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { clearDraft, draftKey, loadDraft, saveDraft } from "@/lib/wmDraft";
import {
    DK_TIMEOUT_MS, dkError, dkLineErrors, dkMessage, fmt, fmtDateTime, fmtDay, fmtQuantity, isIsoDate, loadDestinations,
    shiftDate,
} from "./dkUtils";
import { baseOf, entryFits, closingOf, rebaseEntry, sameEntry, sameText, serverEntry, toPayload } from "./dayModel";
import { DayLine } from "./DayLine";
import { OpeningDialog } from "./OpeningDialog";
import { LocationPicker, NoteBox, OfflineBox, cardClass, chipClass, dataStateText } from "./widgets";

const C = OPS.dk;
const S = OPS.dk.day;

// edit_reason (from GET /dk/day) -> the one sentence shown for it; an unknown
// reason on a locked day falls back to `dk_reason_locked`.
// bedk's `dk_core.edit_state` gives open | review | read_only | inactive | future | closed.
const REASON_KEYS = {
    future: "dk_reason_future", closed: "dk_reason_window", window: "dk_reason_window", too_old: "dk_reason_window",
    outside_window: "dk_reason_window", edit_window: "dk_reason_window", read_only: "dk_reason_read_only",
    no_count: "dk_reason_read_only", not_home: "dk_reason_not_home", not_own: "dk_reason_not_home",
    not_home_location: "dk_reason_not_home", inactive: "dk_reason_inactive",
};

/** The draft holds ONLY unsaved edits; an empty one is removed. */
function persistDraft(key, edits) {
    if (Object.keys(edits).length) saveDraft(key, { edits });
    else clearDraft(key);
}

/**
 * «اليوم»: one location + one business day. The day loads from `GET /dk/day`;
 * the user's UNSAVED edits live in a localStorage draft (wmDraft, kind "dk")
 * until «حفظ» sends only the changed parts of the changed lines, each with the
 * `at` it was typed over (`PUT /dk/day`, all-or-nothing; DK_LINE_STALE when a
 * line was saved elsewhere since: reload, edits kept) — no autosave.
 * `?loc=&date=` preselect (the month grid's «فتح اليوم»). A
 * counter picks a date between `scope.edit_from` and today; a reviewer any
 * day up to today (the server says whether it is editable). A counted 0 is a
 * count; only a blank closing is "not counted".
 */
export function DaySheet({ scope }) {
    const { t, tn, lang } = useI18n();
    const { me } = useAuth();
    const userId = me?.user?.id;
    const [searchParams, setSearchParams] = useSearchParams();

    const caps = scope?.caps || {};
    const locations = useMemo(() => scope?.locations || [], [scope]);
    const today = scope?.today || "";
    const counterOnly = !caps.can_review && !caps.is_master && !caps.can_view_all;
    const minDate = counterOnly ? scope?.edit_from || today : undefined;

    const paramLoc = searchParams.get("loc");
    const paramDate = searchParams.get("date");
    const fallbackLoc = scope?.default_location_id || (locations.find((l) => l.is_active !== false) || locations[0])?.id || "";
    const locationId = locations.some((l) => l.id === paramLoc) ? paramLoc : fallbackLoc;
    const dateOk = (d) => isIsoDate(d) && (!today || d <= today) && (!minDate || d >= minDate);
    const date = dateOk(paramDate) ? paramDate : today;
    const location = locations.find((l) => l.id === locationId);

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value); else next.delete(key);
        setSearchParams(next, { replace: true });
    };

    // ------------------------------------------------------------------ the day
    const dayKey = `${locationId}|${date}`;
    const [day, setDay] = useState(null);           // {key, data}: data is shown only for its own key
    const [loading, setLoading] = useState(false);
    const [loadErr, setLoadErr] = useState(null);
    const seq = useRef(0);

    const load = useCallback(async ({ quiet } = {}) => {
        if (!locationId || !date) return null;
        const my = ++seq.current;
        if (!quiet) setLoading(true);
        setLoadErr(null);
        try {
            const r = await api.get("/dk/day", { params: { location_id: locationId, date } });
            if (my !== seq.current) return null;
            setDay({ key: `${locationId}|${date}`, data: r.data });
            return r.data;
        } catch (e) {
            if (my === seq.current) setLoadErr(e);
            return null;
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, [locationId, date]);

    useEffect(() => { load(); }, [load]);

    const data = day && day.key === dayKey ? day.data : null;
    // excluded items are listed by the server but never offered for entry
    const allItems = useMemo(() => data?.items || [], [data]);
    const items = useMemo(() => allItems.filter((i) => !i.excluded), [allItems]);
    const excludedN = allItems.length - items.length;
    const lines = useMemo(() => data?.lines || {}, [data]);
    const editable = !!data?.editable;
    const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
    const serverEntries = useMemo(() => {
        const m = {};
        for (const it of items) m[it.id] = serverEntry(it, lines[it.id]);
        return m;
    }, [items, lines]);

    // ------------------------------------------------------------ transfers to
    const [destinations, setDestinations] = useState(locations);
    useEffect(() => {
        let alive = true;
        loadDestinations(scope).then((list) => { if (alive && list) setDestinations(list); });
        return () => { alive = false; };
    }, [scope]);

    // ------------------------------------------------------------ draft edits
    const dkey = draftKey({ userId, branchId: locationId, date, type: "day", kind: "dk" });
    const [edits, setEdits] = useState({});
    const [errors, setErrors] = useState({});       // item_id -> [error]
    const [busy, setBusy] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [override, setOverride] = useState(null); // item | null
    const dkeyRef = useRef(dkey);
    const linesRef = useRef(lines);
    const itemsRef = useRef(itemsById);
    const listRef = useRef(null);
    dkeyRef.current = dkey;
    linesRef.current = lines;
    itemsRef.current = itemsById;

    // `edits` holds what the inputs show; the DRAFT only what differs from the stored line
    const unsavedOf = useCallback((all) => {
        const out = {};
        for (const [id, e] of Object.entries(all)) {
            const it = itemsRef.current.get(id);
            if (!it || !sameEntry(it, e, serverEntry(it, linesRef.current[id]))) out[id] = e;
        }
        return out;
    }, []);

    useEffect(() => {
        const d = loadDraft(dkey);
        const restored = d?.edits && typeof d.edits === "object" ? d.edits : {};
        setEdits(restored);
        setErrors({});
        setSaveFailed(false);
        if (Object.keys(restored).length) toast(t("dk_draft_restored"), { id: "dk-draft-restored" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dkey]);

    // A fresh day drops draft entries it already holds, entries for items no longer
    // listed, and entries whose shape no longer fits the item (pack helper changed).
    // An entry whose line was stored again since it was typed (another device, an
    // opening override, this tab's own save) moves onto the fresh line: the user's
    // changes stay, every part they did not touch shows what is stored now.
    useEffect(() => {
        if (!data) return;
        setEdits((prev) => {
            const ids = Object.keys(prev);
            if (!ids.length) return prev;
            const byId = new Map((data.items || []).filter((i) => !i.excluded).map((i) => [i.id, i]));
            const next = {};
            let changed = false;
            for (const id of ids) {
                const it = byId.get(id);
                const line = (data.lines || {})[id];
                let e = prev[id];
                if (!it || !entryFits(it, e)) { changed = true; continue; }
                if (!e.base || e.base.at !== (line?.at ?? null)) { e = rebaseEntry(it, e, line); changed = true; }
                if (sameText(it, e, serverEntry(it, line))) { changed = true; continue; }
                next[id] = e;
            }
            if (!changed) return prev;
            persistDraft(dkeyRef.current, unsavedOf(next));
            return next;
        });
    }, [data, unsavedOf]);

    // Stable for the memoised cards: the stored lines and the key come from refs.
    const setEntry = useCallback((item, entry) => {
        setSaveFailed(false);
        setErrors((e) => {
            if (!e[item.id]) return e;
            const n = { ...e };
            delete n[item.id];
            return n;
        });
        const line = linesRef.current[item.id];
        const server = serverEntry(item, line);
        setEdits((prev) => {
            const next = { ...prev };
            if (sameText(item, entry, server)) delete next[item.id];
            // the first keystroke on a line records the stored line it is typed over
            else next[item.id] = entry.base ? entry : { ...entry, base: baseOf(item, line) };
            persistDraft(dkeyRef.current, unsavedOf(next));
            return next;
        });
    }, [unsavedOf]);

    const dirtyIds = useMemo(() => Object.keys(edits).filter((id) => {
        const it = itemsById.get(id);
        return it && entryFits(it, edits[id]) && !sameEntry(it, edits[id], serverEntries[id]);
    }), [edits, itemsById, serverEntries]);
    const dirtySet = useMemo(() => new Set(editable ? dirtyIds : []), [dirtyIds, editable]);
    const unsavedLocked = !editable ? dirtyIds.length : 0;

    const entryOf = (it) => (editable && edits[it.id] && entryFits(it, edits[it.id]) ? edits[it.id] : serverEntries[it.id]);
    const countedNow = (it) => (editable ? closingOf(it, entryOf(it)) !== null
        : lines[it.id]?.closing !== null && lines[it.id]?.closing !== undefined);
    const countedN = items.reduce((n, it) => n + (countedNow(it) ? 1 : 0), 0);
    const flagsN = items.reduce((n, it) => n + (!dirtySet.has(it.id) && lines[it.id]?.flag ? 1 : 0), 0);

    // Enter / «التالي» on the phone keyboard moves to the next quantity field.
    const focusNext = useCallback((el) => {
        const all = Array.from(listRef.current?.querySelectorAll("input[data-dk-qty]") || []);
        const i = all.indexOf(el);
        if (i !== -1 && all[i + 1]) all[i + 1].focus();
        else el.blur();
    }, []);

    // ------------------------------------------------------------------- save
    const save = async () => {
        if (!dirtyIds.length) { toast(t("dk_nothing_changed"), { id: "dk-nothing-changed" }); return; }
        const myKey = dkey;
        const myDay = dayKey;
        const snapshot = edits;
        const ids = [...dirtyIds];
        const payloadLines = ids.map((id) => {
            const it = itemsById.get(id);
            return toPayload(it, snapshot[id], snapshot[id].base || baseOf(it, lines[id]));
        });
        setBusy(true);
        setErrors({});
        setSaveFailed(false);
        try {
            const r = await api.put("/dk/day", { location_id: locationId, date, lines: payloadLines },
                                    { timeout: DK_TIMEOUT_MS });
            // the answer IS the day: show it at once (else reload it). A GET still in
            // flight was sent BEFORE this save: it is dropped, or it would show the old lines.
            if (r.data && r.data.items && r.data.lines && dkeyRef.current === myKey) {
                seq.current += 1;
                setLoading(false);
                setDay({ key: myDay, data: r.data });
            }
            // The STORED draft drops what was saved right here: the user may have switched
            // tab meanwhile (this screen unmounted), and a leftover would come back as unsaved.
            const stored = { ...(loadDraft(myKey)?.edits || {}) };
            for (const id of ids) if (stored[id] && sameEntry(itemsById.get(id), stored[id], snapshot[id])) delete stored[id];
            persistDraft(myKey, stored);
            if (dkeyRef.current === myKey) {
                setEdits((prev) => {
                    const next = { ...prev };
                    for (const id of ids) if (prev[id] === snapshot[id]) delete next[id];
                    persistDraft(myKey, unsavedOf(next));
                    return next;
                });
            }
            toast.success(fmt(t("dk_saved_n"), { n: fmtQuantity(payloadLines.length, lang) }));
            if (!(r.data && r.data.items && r.data.lines) && dkeyRef.current === myKey) await load({ quiet: true });
        } catch (e) {
            const le = dkLineErrors(e, payloadLines);
            const det = dkError(e);
            if (dkeyRef.current !== myKey) {
                // the user moved to another day: report it, never paint its errors on that day
                toast.error(dkMessage(e, lang) || t("dk_offline_not_saved"));
            } else if (det?.code === "DK_LINE_STALE" || det?.code === "DK_CONFLICT") {
                // another device (or tab) saved these lines after this screen loaded them: nothing
                // was saved. The fresh day comes in and the edits move onto it (the effect above),
                // the draft keeps them, and the lines that changed say so until touched.
                const note = { code: "DK_LINE_STALE", message_ar: t("dk_line_stale_line"), message_en: t("dk_line_stale_line") };
                const stale = Array.isArray(det.items) && det.items.length ? det.items : ids;
                setErrors(Object.fromEntries(stale.filter((id) => itemsById.has(id)).map((id) => [id, [note]])));
                toast.error(t("dk_line_stale"), { id: "dk-line-stale", duration: 10000 });
                await load({ quiet: true });
            } else if (Object.keys(le).length) {
                setErrors(le);
                toast.error(dkMessage(e, lang) || t("dk_offline_error"));
                const first = Object.keys(le)[0];
                requestAnimationFrame(() => {
                    listRef.current?.querySelector(`[data-testid="${S.card(first)}"]`)
                        ?.scrollIntoView({ block: "center", behavior: "smooth" });
                });
            } else {
                const msg = dkMessage(e, lang);
                if (msg) {
                    toast.error(msg);
                    load({ quiet: true });   // the day may have changed state (window closed): show it as it is
                } else {
                    setSaveFailed(true);
                }
            }
        } finally {
            setBusy(false);
        }
    };

    // ------------------------------------------------------------------ render
    const reasonRaw = data?.edit_reason;
    const reasonText = (() => {
        if (!reasonRaw) return editable ? null : t("dk_reason_locked");
        if (typeof reasonRaw === "object") return lang === "ar" ? reasonRaw.message_ar || reasonRaw.message_en : reasonRaw.message_en || reasonRaw.message_ar;
        const key = REASON_KEYS[reasonRaw];
        if (key) return fmt(t(key), { from: fmtDay(scope?.edit_from, lang) });
        return editable ? null : t("dk_reason_locked");
    })();
    const stateText = dataStateText(data?.data_state, scope, lang, t);
    const coverage = data?.coverage || {};
    const loadMsg = loadErr ? dkMessage(loadErr, lang) : null;
    const yesterday = today ? shiftDate(today, -1) : "";
    const lastSaved = useMemo(() => {
        let best = null;
        for (const ln of Object.values(lines)) if (ln?.at && (!best || ln.at > best.at)) best = ln;
        return best;
    }, [lines]);
    const who = (ref) => (ref && typeof ref === "object" ? ref.name : ref) || "";

    return (
        <div className="space-y-3">
            {/* location + date */}
            <div className={`${cardClass} space-y-2 p-3`}>
                <div className="grid gap-2 sm:grid-cols-2">
                    <LocationPicker locations={locations} value={locationId} disabled={busy}
                                    onChange={(v) => setParam("loc", v)} />
                    <Input data-testid={C.dateInput} type="date" value={date} dir="ltr" lang="ar"
                           min={minDate || undefined} max={today || undefined} disabled={busy}
                           onChange={(e) => { if (dateOk(e.target.value)) setParam("date", e.target.value); }}
                           className="h-10 text-base" />
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    <button type="button" data-testid={C.dateChip("today")} data-active={date === today ? "true" : "false"}
                            disabled={busy} onClick={() => setParam("date", today)} className={chipClass(date === today)}>
                        {t("dk_today")}
                    </button>
                    {yesterday && dateOk(yesterday) ? (
                        <button type="button" data-testid={C.dateChip("yesterday")} data-active={date === yesterday ? "true" : "false"}
                                disabled={busy} onClick={() => setParam("date", yesterday)} className={chipClass(date === yesterday)}>
                            {t("dk_yesterday")}
                        </button>
                    ) : null}
                    {counterOnly && scope?.edit_from ? (
                        <span className="flex items-center gap-1 text-[11px] text-[color:var(--ink-faint)]">
                            <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                            {fmt(t("dk_edit_from_hint"), { from: fmtDay(scope.edit_from, lang) })}
                        </span>
                    ) : null}
                </div>
            </div>

            {!data && loading ? <InlineLoader label={t("dk_loading")} /> : null}
            {!data && !loading && loadErr
                ? (loadMsg ? <NoteBox>{loadMsg}</NoteBox> : <OfflineBox onRetry={() => load()} busy={loading} />)
                : null}

            {data ? (
                <div data-testid={S.form} data-editable={editable ? "true" : "false"} data-edit-reason={typeof reasonRaw === "string" ? reasonRaw : ""}
                     data-state={data.data_state || ""} data-date={date}
                     className={`${cardClass} p-3 transition-opacity duration-150 ${loading ? "opacity-60" : ""}`}>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 truncate text-sm font-bold text-[color:var(--ink)]">{location ? tn(location) : ""}</span>
                        <span className="text-xs font-semibold text-[color:var(--ink-muted)]"><bdi>{fmtDay(date, lang)}</bdi></span>
                        {lastSaved ? (
                            <span className="w-full text-[11px] text-[color:var(--ink-faint)] sm:ms-auto sm:w-auto">
                                {fmt(t("dk_stamp_saved"), { by: who(lastSaved.by) })} <bdi dir="ltr">{fmtDateTime(lastSaved.at, lang)}</bdi>
                            </span>
                        ) : null}
                    </div>

                    {reasonText ? (
                        editable ? (
                            <p data-testid={S.editReason} className="mt-2 flex items-start gap-1.5 text-[11px] font-semibold text-[color:var(--ink-muted)]">
                                <Info className="mt-px h-3.5 w-3.5 shrink-0" />{reasonText}
                            </p>
                        ) : (
                            <div data-testid={S.editReason} className="mt-2 flex items-start gap-2 rounded-[var(--radius-sm)] bg-[color:var(--bg-surface-2)] px-3 py-2">
                                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--ink-muted)]" />
                                <p className="text-xs font-semibold text-[color:var(--ink-muted)]">{reasonText}</p>
                            </div>
                        )
                    ) : null}
                    {unsavedLocked ? (
                        <p className="mt-2 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                            {fmt(t("dk_draft_locked"), { n: fmtQuantity(unsavedLocked, lang) })}
                        </p>
                    ) : null}

                    {/* the POS sales behind «حسب المبيعات» */}
                    {stateText || coverage.unmatched_qty || coverage.unit_issues ? (
                        <div data-testid={S.dataState} data-state={data.data_state || ""}
                             className="mt-2 space-y-1 rounded-[var(--radius-sm)] bg-[color:var(--warning-soft)] px-3 py-2 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                            {stateText ? (
                                <p className="flex items-start gap-1.5"><ShoppingBag className="mt-px h-3.5 w-3.5 shrink-0" />{stateText}</p>
                            ) : null}
                            {coverage.unmatched_qty ? (
                                <p data-testid={S.coverage} data-qty={coverage.unmatched_qty}>
                                    {fmt(t("dk_coverage_unmatched"), { n: fmtQuantity(coverage.unmatched_qty, lang) })}
                                    {(coverage.unmatched_names || []).length ? (
                                        <span className="block font-normal opacity-90">{coverage.unmatched_names.slice(0, 10).join("، ")}</span>
                                    ) : null}
                                </p>
                            ) : null}
                            {coverage.unit_issues ? (
                                <p>{fmt(t("dk_coverage_unit_issues"), { n: fmtQuantity(coverage.unit_issues, lang) })}</p>
                            ) : null}
                        </div>
                    ) : null}

                    <div ref={listRef} className="mt-3 space-y-2.5">
                        {!items.length ? (
                            <p className="py-10 text-center text-sm text-[color:var(--ink-muted)]">{t("dk_no_items")}</p>
                        ) : items.map((it) => (
                            <DayLine key={it.id} item={it} line={lines[it.id]} entry={entryOf(it)} dirty={dirtySet.has(it.id)}
                                     errors={errors[it.id]} editable={editable} scope={scope} date={date}
                                     destinations={destinations} locationId={locationId} dataState={data.data_state}
                                     onChange={setEntry} onEnter={focusNext} onOverride={editable ? setOverride : null} />
                        ))}
                        {excludedN ? (
                            <p className="text-center text-[11px] text-[color:var(--ink-faint)]">
                                {fmt(t("dk_excluded_n"), { n: fmtQuantity(excludedN, lang) })}
                            </p>
                        ) : null}
                    </div>

                    {saveFailed ? <div className="mt-3"><OfflineBox onRetry={save} busy={busy} text={t("dk_offline_not_saved")} /></div> : null}
                    {!saveFailed && loadErr && !loading ? (
                        <div className="mt-3">
                            {loadMsg ? <NoteBox>{loadMsg}</NoteBox> : <OfflineBox onRetry={() => load({ quiet: true })} busy={loading} />}
                        </div>
                    ) : null}

                    {/* the bar: progress, flags, unsaved lines, save */}
                    <div className="sticky bottom-0 z-10 mt-3 space-y-2 border-t border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] pt-2">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-[color:var(--ink-muted)]">
                            <span data-testid={S.progress} data-counted={countedN} data-total={items.length}>
                                <bdi dir="ltr">{fmtQuantity(countedN, lang)}/{fmtQuantity(items.length, lang)}</bdi> {t("dk_counted_suffix")}
                            </span>
                            {flagsN ? (
                                <span data-testid={S.flagsN} data-count={flagsN} className="text-[color:var(--danger-soft-fg)]">
                                    {fmt(t("dk_flags_n"), { n: fmtQuantity(flagsN, lang) })}
                                </span>
                            ) : null}
                            {dirtySet.size ? (
                                <span data-testid={S.dirty} data-count={dirtySet.size}
                                      className="flex items-center gap-1.5 text-[color:var(--brand-orange)]">
                                    <span aria-hidden className="h-2 w-2 rounded-full bg-[color:var(--brand-orange)]" />
                                    {fmt(t("dk_unsaved_n"), { n: fmtQuantity(dirtySet.size, lang) })}
                                </span>
                            ) : null}
                        </div>
                        {editable ? (
                            <Button data-testid={S.save} onClick={save} disabled={busy || !dirtySet.size}
                                    className="h-11 w-full gap-2 text-sm font-bold">
                                <Save className="h-4 w-4" />
                                {busy ? t("dk_saving") : t("dk_save")}
                            </Button>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <OpeningDialog open={!!override} onOpenChange={(o) => { if (!o) setOverride(null); }}
                           locationId={locationId} date={date} item={override}
                           line={override ? lines[override.id] : null}
                           onDone={() => load({ quiet: true })} />
        </div>
    );
}
